/**
 * Selectable list rows: **big** (featured icon + title) vs **small** (title + subtitle + radio).
 * Segmented control option style matches the small selectable border/fill language.
 *
 * **Consumer map:** `src/version1/journey/JourneyScreen.tsx` (goal step + activity step + gender segment); `src/version1/customer/AllergiesDislikesScreen.tsx` (tabs + preference rows) — v2/v3 use equivalent paths under `version2|3/…`.
 * Pair with `Clickable`.
 *
 * @see atomicRules.ts
 */
import { ViewStyle, TextStyle } from 'react-native';
import { hexToRgba } from '../colorUtils';
import type { ThemeColors } from '../store';
import { appTheme } from '../appTheme';
import { centeredLabelText } from './buttons';

const BIG_BORDER = 1;
const SMALL_BORDER = 1;

export const selectableItemListGap: ViewStyle = {
  gap: appTheme.spacing.xl,
};

export const selectableItemBigRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
};

/** Title column beside the icon disc in `selectableItemBigRow`. Used by: `JourneyScreen` goal row. */
export const selectableItemBigTextColumn: ViewStyle = {
  flex: 1,
  minWidth: 0,
  justifyContent: 'center',
};

export const selectableItemSmallRow: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
};

export const selectableItemSmallContent: ViewStyle = {
  flex: 1,
  minWidth: 0,
  paddingEnd: appTheme.spacing.sm,
};

/** Spacer when no trailing check icon (keeps title alignment). */
export const selectableItemTrailingSpacer: ViewStyle = {
  width: 24,
  height: 24,
};

export function createSelectableBigIconDiscStyle(
  c: ThemeColors,
  opts: { selected: boolean; accentColor: string },
): ViewStyle {
  return {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: opts.selected ? hexToRgba(opts.accentColor, 0.22) : hexToRgba(c.border, 0.45),
    backgroundColor: opts.selected ? hexToRgba(opts.accentColor, 0.1) : hexToRgba(c.text, 0.06),
  };
}

/**
 * Big selectable surface (featured row + icon disc).
 * @param accentColor — brand tint per option (semantic accent hex).
 */
export function createSelectableItemBigSurfaceStyle(
  c: ThemeColors,
  opts: { selected: boolean; accentColor: string },
): ViewStyle {
  return {
    borderRadius: appTheme.borderRadius.xl,
    borderWidth: BIG_BORDER,
    padding: appTheme.card.padding.xl,
    shadowColor: c.shadowColor,
    borderColor: opts.selected ? opts.accentColor : hexToRgba(c.border, 0.75),
    backgroundColor: opts.selected ? hexToRgba(opts.accentColor, 0.08) : c.surface,
    ...(opts.selected ? appTheme.shadows.md : appTheme.shadows.card),
  };
}

export function createSelectableItemBigTitleStyle(
  c: ThemeColors,
  opts: { selected: boolean; accentColor: string },
): TextStyle {
  return {
    fontSize: 19,
    lineHeight: 26,
    fontWeight: '700',
    letterSpacing: -0.3,
    color: opts.selected ? opts.accentColor : c.text,
  };
}

/** Small selectable surface (title + subtitle + radio). */
export function createSelectableItemSmallSurfaceStyle(
  c: ThemeColors,
  opts: { selected: boolean },
): ViewStyle {
  return {
    paddingVertical: appTheme.spacing.lg,
    paddingHorizontal: appTheme.card.padding.xl,
    borderRadius: appTheme.borderRadius.xl,
    borderWidth: SMALL_BORDER,
    shadowColor: c.shadowColor,
    borderColor: opts.selected ? c.primary : hexToRgba(c.border, 0.75),
    backgroundColor: opts.selected ? c.primaryUltra : c.surface,
    ...(opts.selected ? appTheme.shadows.md : appTheme.shadows.card),
  };
}

export function createSelectableItemSmallTitleStyle(
  c: ThemeColors,
  opts: { selected: boolean },
): TextStyle {
  return {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '700',
    letterSpacing: 0.1,
    color: opts.selected ? c.primary : c.text,
  };
}

export function createSelectableItemSmallSubtitleStyle(c: ThemeColors): TextStyle {
  return {
    fontSize: 13,
    lineHeight: 20,
    fontWeight: '400',
    marginTop: 6,
    color: c.textSecondary,
  };
}

export function createSelectableRadioOuterStyle(c: ThemeColors, opts: { selected: boolean }): ViewStyle {
  return {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: opts.selected ? c.primary : hexToRgba(c.border, 0.85),
    justifyContent: 'center',
    alignItems: 'center',
  };
}

export function createSelectableRadioInnerStyle(c: ThemeColors): ViewStyle {
  return {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: c.primary,
  };
}

/** Segmented two-option control (compact tap targets). */
export function createSegmentedGenderOptionStyle(c: ThemeColors, opts: { selected: boolean }): ViewStyle {
  return {
    flex: 1,
    alignSelf: 'stretch',
    minHeight: 40,
    paddingVertical: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.sm,
    borderRadius: appTheme.borderRadius.lg,
    borderWidth: SMALL_BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: c.shadowColor,
    borderColor: opts.selected ? c.primary : hexToRgba(c.border, 0.75),
    backgroundColor: opts.selected ? c.primaryUltra : c.surface,
    ...appTheme.shadows.sm,
  };
}

export function createSegmentedGenderLabelStyle(c: ThemeColors, opts: { selected: boolean }): TextStyle {
  return {
    ...centeredLabelText,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '700',
    color: opts.selected ? c.primary : c.text,
  };
}

export const segmentedGenderRow: ViewStyle = {
  flexDirection: 'row',
  gap: appTheme.spacing.md,
};
